import { Card } from "@design";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { BrandResponse } from "@/api";
import { Text } from "@/components/Text";
import { helpHref } from "@/features/help/articles";
import { TemplateSlot } from "./TemplateSlot";
import { TEMPLATE_FORMATS } from "./useBrandTemplates";

const GOOD_POINTS = ["layouts", "styles", "cellStyles", "theme", "empty"] as const;

/**
 * Brand > Templates: the brand's own PowerPoint, Word, and Excel templates (one per
 * format). Chat's generated decks, documents, and workbooks start from them when this
 * brand is active.
 */
export function BrandTemplatesTab({ brand }: Readonly<{ brand: BrandResponse }>) {
  const { t } = useTranslation("brands", { keyPrefix: "templates" });
  const templates = brand.templates ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-col gap-3">
          <Text variant="body-sm" tone="secondary">
            {t("intro")}
          </Text>
          <Text as="h3" variant="eyebrow" tone="secondary">
            {t("goodTitle")}
          </Text>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {GOOD_POINTS.map((point) => (
              <li key={point}>
                <Text as="span" variant="body-sm" tone="secondary">
                  {t(`good.${point}`)}
                </Text>
              </li>
            ))}
          </ul>
          <Link to={helpHref("brands", "templates")} className="self-start text-content-primary underline">
            <Text as="span" variant="body-sm">
              {t("learnMore")}
            </Text>
          </Link>
        </div>
      </Card>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {TEMPLATE_FORMATS.map((format) => (
          <TemplateSlot
            key={format}
            brandUuid={brand.uuid}
            format={format}
            template={templates.find((tpl) => tpl.format === format)}
            canEdit={brand.canEdit ?? false}
          />
        ))}
      </div>
    </div>
  );
}
